import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';
import { sessionsApi } from '../../services/api/sessions.api';
import { AdminBuildStamp } from '../../components/admin/AdminBuildStamp';
import {
  buildMonthlySeries,
  exportMonthCsv,
  exportPeriodCsv,
  filterPlayedSessions,
  monthKeyFromDate,
  normalizeSessions,
  parseMonthKey,
  summarizePeriod,
  type MonthStatsEntry,
  type MonthlySeriesRange,
} from './sessionStats';

type RangePreset = '6' | '12' | '24' | 'all' | 'custom';

const PRESET_LABELS: Record<Exclude<RangePreset, 'custom'>, string> = {
  '6': '6 mois',
  '12': '12 mois',
  '24': '24 mois',
  all: 'Tout l’historique',
};

const toMonthInputValue = (date: Date): string => monthKeyFromDate(date);

export const AdminSessionStatsPage: React.FC = () => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const [rangePreset, setRangePreset] = useState<RangePreset>('12');
  const [customFrom, setCustomFrom] = useState(() => {
    const now = new Date();
    return toMonthInputValue(new Date(now.getFullYear(), now.getMonth() - 11, 1));
  });
  const [customTo, setCustomTo] = useState(() => toMonthInputValue(new Date()));
  const [appliedCustomRange, setAppliedCustomRange] = useState<{ from: string; to: string } | null>(null);
  const [allPlayedSessions, setAllPlayedSessions] = useState<ReturnType<typeof filterPlayedSessions>>([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await sessionsApi.getAllSessions(false, true);
        setAllPlayedSessions(filterPlayedSessions(normalizeSessions(data)));
      } catch (error) {
        console.error('Failed to load sessions for stats page:', error);
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, []);

  const seriesRange = useMemo((): MonthlySeriesRange => {
    if (rangePreset === 'custom' && appliedCustomRange) {
      return {
        type: 'between',
        from: parseMonthKey(appliedCustomRange.from),
        to: parseMonthKey(appliedCustomRange.to),
      };
    }
    if (rangePreset === 'all') {
      return { type: 'all' };
    }
    if (rangePreset === 'custom') {
      return { type: 'last', count: 12 };
    }
    return { type: 'last', count: Number(rangePreset) };
  }, [appliedCustomRange, rangePreset]);

  const monthlySeries = useMemo(
    () => (loading ? [] : buildMonthlySeries(allPlayedSessions, seriesRange)),
    [allPlayedSessions, loading, seriesRange]
  );

  const periodSummary = useMemo(() => summarizePeriod(monthlySeries), [monthlySeries]);

  const periodExportKeys = useMemo(() => {
    if (monthlySeries.length === 0) {
      return null;
    }
    return {
      from: monthlySeries[0].key,
      to: monthlySeries[monthlySeries.length - 1].key,
    };
  }, [monthlySeries]);

  const applyCustomRange = () => {
    if (!customFrom || !customTo) {
      return;
    }
    setAppliedCustomRange({ from: customFrom, to: customTo });
    setRangePreset('custom');
  };

  useEffect(() => {
    const destroyChart = () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };

    if (loading || monthlySeries.length === 0) {
      destroyChart();
      return;
    }

    let cancelled = false;
    const frameId = requestAnimationFrame(() => {
      if (cancelled || !chartRef.current || monthlySeries.length === 0) {
        return;
      }

      import('apexcharts').then((ApexChartsModule) => {
        if (cancelled || !chartRef.current) {
          return;
        }

        const ApexCharts = ApexChartsModule.default;
        destroyChart();

        const chart = new ApexCharts(chartRef.current, {
          series: [
            { name: 'Sessions', data: monthlySeries.map((item) => item.sessions) },
            { name: 'Joueurs', data: monthlySeries.map((item) => item.players) },
            { name: 'Équipes terminées', data: monthlySeries.map((item) => item.completed) },
          ],
          chart: {
            type: 'line',
            height: 360,
            fontFamily: 'Inter, sans-serif',
            toolbar: { show: false },
            background: 'transparent',
          },
          stroke: {
            curve: 'smooth',
            width: 3,
          },
          colors: ['#4f46e5', '#0f172a', '#10b981'],
          xaxis: {
            categories: monthlySeries.map((item) => item.label),
            labels: {
              rotate: monthlySeries.length > 12 ? -45 : 0,
              style: {
                colors: '#64748b',
                fontSize: '11px',
                fontFamily: 'Inter, sans-serif',
              },
            },
          },
          yaxis: {
            labels: {
              style: {
                colors: '#64748b',
                fontSize: '11px',
                fontFamily: 'Inter, sans-serif',
              },
            },
          },
          grid: {
            borderColor: '#e2e8f0',
            strokeDashArray: 4,
          },
          legend: {
            position: 'top',
            horizontalAlign: 'left',
            fontFamily: 'Inter, sans-serif',
          },
          tooltip: {
            theme: 'light',
          },
        } as any);

        chart.render();
        chartInstanceRef.current = chart;
      });
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
      destroyChart();
    };
  }, [monthlySeries, loading]);

  const handleExportPeriod = () => {
    if (!periodExportKeys) {
      return;
    }
    exportPeriodCsv(allPlayedSessions, periodExportKeys.from, periodExportKeys.to);
  };

  const reversedSeries = useMemo(() => [...monthlySeries].reverse(), [monthlySeries]);

  return (
    <div className="flex min-h-full flex-col font-sans text-slate-900">
      <main className="flex-grow w-full px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 w-full rounded-[2rem] border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-slate-50 p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <Link
                to="/admin/sessions"
                className="inline-flex items-center gap-2 text-sm font-bold text-violet-700 hover:text-violet-900"
              >
                <ArrowLeft className="h-4 w-4" />
                Retour aux sessions
              </Link>
              <p className="mt-4 text-xs font-black uppercase tracking-[0.25em] text-violet-600">Super Admin</p>
              <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-900">Statistiques détaillées</h1>
              <p className="mt-2 max-w-3xl text-sm text-slate-600">
                Explorez l’historique complet des sessions jouées, ajustez la période d’analyse et exportez des
                rapports mensuels ou sur toute une plage de dates.
              </p>
            </div>
            {periodExportKeys && periodSummary.sessions > 0 && (
              <button
                type="button"
                onClick={handleExportPeriod}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-indigo-200 hover:bg-indigo-700"
              >
                <Download className="h-4 w-4" />
                Exporter la période (CSV)
              </button>
            )}
          </div>

          <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-5">
            <div className="text-sm font-black uppercase tracking-wider text-slate-500">Période d’analyse</div>
            <div className="mt-4 flex flex-wrap gap-2">
              {(Object.keys(PRESET_LABELS) as Array<Exclude<RangePreset, 'custom'>>).map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => {
                    setRangePreset(preset);
                    setAppliedCustomRange(null);
                  }}
                  className={`rounded-xl px-4 py-2 text-sm font-bold transition-colors ${
                    rangePreset === preset
                      ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200'
                      : 'border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {PRESET_LABELS[preset]}
                </button>
              ))}
            </div>

            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-400">Du</label>
                <input
                  type="month"
                  value={customFrom}
                  onChange={(event) => setCustomFrom(event.target.value)}
                  className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-500/10"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-400">Au</label>
                <input
                  type="month"
                  value={customTo}
                  onChange={(event) => setCustomTo(event.target.value)}
                  className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-500/10"
                />
              </div>
              <button
                type="button"
                onClick={applyCustomRange}
                className={`rounded-xl px-4 py-2.5 text-sm font-bold transition-colors ${
                  rangePreset === 'custom'
                    ? 'bg-violet-600 text-white'
                    : 'border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100'
                }`}
              >
                Appliquer la période personnalisée
              </button>
            </div>
          </div>

          {loading ? (
            <div className="mt-8 text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600" />
              <p className="mt-4 text-slate-500">Chargement des statistiques...</p>
            </div>
          ) : (
            <>
              <div className="mt-6 grid grid-cols-2 gap-4 xl:grid-cols-5">
                <SummaryCard label="Mois couverts" value={periodSummary.months} />
                <SummaryCard label="Mois actifs" value={periodSummary.activeMonths} accent="text-violet-600" />
                <SummaryCard label="Sessions jouées" value={periodSummary.sessions} />
                <SummaryCard label="Joueurs cumulés" value={periodSummary.players} />
                <SummaryCard label="Équipes terminées" value={periodSummary.completed} accent="text-emerald-600" />
              </div>

              <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-5">
                <div className="mb-4">
                  <div className="text-sm font-black uppercase tracking-wider text-slate-500">Courbes mensuelles</div>
                  <div className="mt-1 text-sm text-slate-500">
                    {monthlySeries.length} mois affichés, y compris les mois sans activité.
                  </div>
                </div>
                {monthlySeries.length > 0 ? (
                  <div ref={chartRef} />
                ) : (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-sm text-slate-500">
                    Aucune période sélectionnée.
                  </div>
                )}
              </div>

              <div className="mt-6 rounded-3xl border border-slate-200 bg-white p-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="text-sm font-black uppercase tracking-wider text-slate-500">Rapports mensuels</div>
                    <div className="mt-1 text-sm text-slate-500">
                      Téléchargez un export CSV pour chaque mois de la période.
                    </div>
                  </div>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-400">
                    {reversedSeries.length} mois listés
                  </div>
                </div>

                <div className="mt-4 overflow-hidden rounded-2xl border border-slate-100">
                  <table className="min-w-full divide-y divide-slate-100">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Mois</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Sessions</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Joueurs</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Terminées</th>
                        <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400">Archivées</th>
                        <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-slate-400">Export</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {reversedSeries.map((item) => (
                        <MonthReportRow
                          key={item.key}
                          item={item}
                          onExport={() => exportMonthCsv(allPlayedSessions, item.key)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          <div className="mt-6 flex justify-end">
            <AdminBuildStamp />
          </div>
        </div>
      </main>
    </div>
  );
};

const SummaryCard: React.FC<{ label: string; value: number; accent?: string }> = ({
  label,
  value,
  accent = 'text-slate-900',
}) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4">
    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
    <div className={`mt-2 text-3xl font-black ${accent}`}>{value}</div>
  </div>
);

const MonthReportRow: React.FC<{ item: MonthStatsEntry; onExport: () => void }> = ({ item, onExport }) => (
  <tr className={item.sessions === 0 ? 'bg-slate-50/70' : undefined}>
    <td className="px-4 py-3 text-sm font-bold text-slate-900">{item.label}</td>
    <td className="px-4 py-3 text-sm text-slate-600">{item.sessions}</td>
    <td className="px-4 py-3 text-sm text-slate-600">{item.players}</td>
    <td className="px-4 py-3 text-sm text-slate-600">{item.completed}</td>
    <td className="px-4 py-3 text-sm text-slate-600">{item.archived}</td>
    <td className="px-4 py-3 text-right">
      {item.sessions > 0 ? (
        <button
          type="button"
          onClick={onExport}
          className="inline-flex items-center gap-1.5 rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-bold text-indigo-700 hover:bg-indigo-100"
        >
          <Download className="h-3.5 w-3.5" />
          CSV
        </button>
      ) : (
        <span className="text-xs font-semibold text-slate-300">—</span>
      )}
    </td>
  </tr>
);
